// Offer What We Do Component
// Displays the "What we do" section of an offer card

import { OFFERS } from "./offers-config.ts";
import type { AdditionalFocusItem, OfferItem } from "./offers-config.ts";

class OfferWhatWeDo extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  private createCheckIcon(color: string): string {
    return `
      <svg class="w-5 h-5 text-${color} mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd" />
      </svg>
    `;
  }

  private renderAdditionalFocusItems(items: AdditionalFocusItem[]): string {
    return items.map((item, index) => {
      const price = item.price ? ` €${item.price}` : "";
      const hasDetails = item.description || item.scope;
      const itemId = `focus-item-${index}`;

      let detailsHtml = "";
      if (hasDetails) {
        let scopeHtml = "";
        if (item.scope && item.scope.length > 0) {
          const scopeItems = item.scope.map(scopeItem =>
            `<li class="text-slate-600">
              <span data-en="${scopeItem.en}" data-lv="${scopeItem.lv}" data-it="${scopeItem.it}">
                ${scopeItem.en}
              </span>
            </li>`
          ).join("");
          scopeHtml = `
            <div class="mt-2">
              <strong class="text-slate-700">Scope:</strong>
              <ul class="list-disc ml-5 mt-1 space-y-1">
                ${scopeItems}
              </ul>
            </div>
          `;
        }

        detailsHtml = `
          <div id="${itemId}" class="hidden mt-2 pl-4 border-l-2 border-slate-200 text-sm">
            ${item.description ? `
              <p class="text-slate-600 mb-2">
                <span data-en="${item.description.en}" data-lv="${item.description.lv}" data-it="${item.description.it}">
                  ${item.description.en}
                </span>
              </p>
            ` : ""}
            ${scopeHtml}
          </div>
        `;
      }

      const expandButton = hasDetails ? `
        <button
          class="ml-2 text-blue-600 hover:text-blue-800 text-xs focus:outline-none focus-item-toggle"
          data-target="${itemId}"
          aria-expanded="false"
          aria-label="Toggle details"
        >
          <svg class="w-4 h-4 inline transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
      ` : "";

      return `
        <li class="focus-item">
          <div class="flex items-center">
            <span data-en="${item.text.en}" data-lv="${item.text.lv}" data-it="${item.text.it}">${item.text.en}</span>${price}${expandButton}
          </div>
          ${detailsHtml}
        </li>
      `;
    }).join("\n");
  }

  private renderItem(item: OfferItem, color: string): string {
    // Skip items with additionalFocusItems since they're now in a separate box
    if (item.additionalFocusItems) {
      return "";
    }

    const descriptionHtml = item.description
      ? `
      <div class="text-slate-600">
        <span data-en="${item.description.en}" data-lv="${item.description.lv}" data-it="${item.description.it}">
          ${item.description.en}
        </span>
      </div>
    `
      : "";

    return `
      <li class="flex items-start gap-3">
        ${this.createCheckIcon(color)}
        <div>
          <div class="text-slate-600">
            <span data-en="${item.text.en}" data-lv="${item.text.lv}" data-it="${item.text.it}">
              ${item.text.en}
            </span>
          </div>
          ${descriptionHtml}
        </div>
      </li>
    `;
  }

  private render() {
    const offerId = this.getAttribute("offer-id");
    const color = this.getAttribute("color") || "blue-600";

    if (!offerId) {
      console.error("OfferWhatWeDo: offer-id attribute is required");
      return;
    }

    const offer = OFFERS.find((o) => o.id === offerId);
    if (!offer) {
      console.error(`OfferWhatWeDo: offer with id "${offerId}" not found`);
      return;
    }

    const itemsHtml = offer.whatWeDo.map((item) => this.renderItem(item, color)).join("\n");

    this.innerHTML = `
      <ul class="space-y-3 mb-4">
        ${itemsHtml}
      </ul>
    `;

    // Add event listeners for expand/collapse buttons
    this.querySelectorAll(".focus-item-toggle").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const target = (button as HTMLElement).getAttribute("data-target");
        if (target) {
          const detailsElement = this.querySelector(`#${target}`);
          const svgIcon = button.querySelector("svg");

          if (detailsElement) {
            const isHidden = detailsElement.classList.contains("hidden");

            if (isHidden) {
              detailsElement.classList.remove("hidden");
              button.setAttribute("aria-expanded", "true");
              if (svgIcon) {
                svgIcon.style.transform = "rotate(180deg)";
              }
            } else {
              detailsElement.classList.add("hidden");
              button.setAttribute("aria-expanded", "false");
              if (svgIcon) {
                svgIcon.style.transform = "rotate(0deg)";
              }
            }
          }
        }
      });
    });
  }
}

customElements.define("offer-what-we-do", OfferWhatWeDo);

export { OfferWhatWeDo };

